import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

const d = (offsetDays: number) =>
  new Date(Date.now() + offsetDays * 86_400_000).toISOString();

export async function seedINPRSProject(token: string): Promise<string> {
  const auth = { Authorization: `Bearer ${token}` };

  // ── 1. Project ──────────────────────────────────────────────────────────────
  const { data: project } = await axios.post(
    `${BASE}/api/projects`,
    {
      name: "INPRS Salesforce CRM Implementation — RFP 23-03",
      description:
        "Real public-sector RFP: Indiana Public Retirement System (INPRS) soliciting Salesforce Service Cloud CRM implementation services. Phase 1 (this award): core case management, workflow automation, and integration to INPAS/ERM/Voya via MuleSoft. Phases 2-3 (contract-extension options): Employer Portal and Member Portal.",
      status: "active",
      methodology: "agile",
      sprintLength: 2,

      diagramText: `flowchart TD
  A[RFP 23-03 Released — Sep 26 2023] --> B[Intent to Bid / Inquiries]
  B --> C[Proposals Due Nov 3 2023]
  C --> D[Finalist Presentations Dec 18 2023]
  D --> E[Vendor Selected Jan 3 2024]
  E --> F[Phase 1: Core CRM + Case Mgmt]
  F --> G[MuleSoft Integrations — INPAS, ERM, Voya]
  G --> H{Data Governance Council Approval}
  H -->|Approved| I[Phase 1 UAT + Go-Live]
  H -->|Rejected| G
  I --> J[Phase 2 Option: Employer Portal + Telephony]
  J --> K[Phase 3 Option: Member Portal + Self-Service]`,

      stakeholders: [
        {
          id: "sh-1",
          name: "Stanton Lanman",
          role: "Director of Vendor Management and Procurement",
          team: "INPRS Procurement",
          email: "procurements@inprs.in.gov",
          timezone: "EDT",
          notes: "Named RFP contact. All vendor inquiries route through him per RFP §1.11. Direct contact with Board of Trustees is prohibited during the solicitation.",
          interest: "Clean, compliant procurement process; fiscal responsibility",
          influence: "high",
        },
        {
          id: "sh-2",
          name: "Data Governance Council",
          role: "Data Strategy Approval Body",
          team: "INPRS IT / Data",
          email: "",
          timezone: "EDT",
          notes: "Any data-related decision (golden-record/MDM design, PII access mapping) must be approved here per RFP §2.3.",
          interest: "Single source-of-truth architecture, no fragmented data ownership",
          influence: "high",
        },
        {
          id: "sh-3",
          name: "Voya Financial",
          role: "Tier 1 Record Keeper (Third-Party Integration Partner)",
          team: "External",
          email: "",
          timezone: "",
          notes: "Maintains a separate Salesforce instance for Tier 1 member support. INPRS's instance must stay in sync with Voya's — this is the highest-risk integration in the RFP (§3.4).",
          interest: "Reliable, low-friction data sync between the two Salesforce orgs",
          influence: "high",
        },
        {
          id: "sh-4",
          name: "Member Advocates",
          role: "Frontline Case Users",
          team: "INPRS Staff",
          email: "",
          timezone: "EDT",
          notes: "Handle pension-related member cases day to day. Primary users of case creation, escalation, and knowledge base.",
          interest: "Fast case resolution, low tool friction, reliable SLA alerts",
          influence: "medium",
        },
        {
          id: "sh-5",
          name: "Employer Advocates",
          role: "Employer-Facing Case Users",
          team: "INPRS Staff",
          email: "",
          timezone: "EDT",
          notes: "Handle employer-side cases; primary users of the Phase 2 Employer Portal once built.",
          interest: "Unified employer reporting + case visibility",
          influence: "medium",
        },
        {
          id: "sh-6",
          name: "SFDC Administrators",
          role: "Internal Salesforce Admins",
          team: "INPRS IT",
          email: "",
          timezone: "EDT",
          notes: "INPRS staff take over 100% Salesforce administration after the engagement (RFP §2.4) — vendor must fully train them during Phase 1, including Phase 2/3 skills.",
          interest: "Full admin capability transfer, no vendor lock-in",
          influence: "high",
        },
      ],

      raciMatrix: [
        {
          id: "raci-1",
          activity: "Vendor proposal evaluation & selection",
          assignments: { "sh-1": "A", "sh-2": "I", "sh-3": "I", "sh-4": "I", "sh-5": "I", "sh-6": "C" },
        },
        {
          id: "raci-2",
          activity: "Data strategy / MDM / golden-record design approval",
          assignments: { "sh-1": "I", "sh-2": "A", "sh-3": "C", "sh-4": "I", "sh-5": "I", "sh-6": "R" },
        },
        {
          id: "raci-3",
          activity: "Voya ↔ INPRS Salesforce sync design & testing",
          assignments: { "sh-1": "I", "sh-2": "C", "sh-3": "R", "sh-4": "I", "sh-5": "I", "sh-6": "A" },
        },
        {
          id: "raci-4",
          activity: "Phase 1 UAT sign-off",
          assignments: { "sh-1": "A", "sh-2": "C", "sh-3": "I", "sh-4": "R", "sh-5": "R", "sh-6": "C" },
        },
        {
          id: "raci-5",
          activity: "Salesforce admin knowledge transfer",
          assignments: { "sh-1": "I", "sh-2": "I", "sh-3": "I", "sh-4": "I", "sh-5": "I", "sh-6": "A" },
        },
      ],

      documents: [
        {
          id: "doc-brd",
          type: "brd",
          title: "Project Goals & Future State Vision — RFP 23-03",
          content: `# Project Goals & Future State Vision
**Agency:** Indiana Public Retirement System (INPRS)
**RFP:** 23-03 — Salesforce Customer Relationship Management Implementation Services
**Released:** September 26, 2023 | **Proposals Due:** November 3, 2023

## Agency Background
INPRS was established by Indiana statute in 2011 as an independent body corporate and politic — not a state department or agency, but an independent instrumentality administering 16 retirement funds across Defined Benefit, Defined Contribution, OPEB, and Custodial fund categories (PERF, TRF, '77 Fund, JRS, EG&C, PARF, LE DB/DC, SDBF, RMBA, LPSPR).

## Purpose (RFP §1.4)
INPRS's business processes currently involve significant manual effort. This RFP commissions analysis of existing workflows and implementation of Salesforce as a SaaS solution for Workflow and Case Management to achieve INPRS's "Future State" goals.

## Project Goals (RFP §1.5)
1. Implement Salesforce Service Cloud to streamline and automate case management across the organization
2. Achieve full transactional-level integration across as many business processes as possible
3. Adhere to a **1% or less customization** standard — configure existing Salesforce elements rather than custom-build
4. Deploy all system integrations via **MuleSoft** for secure, efficient data flow

## Three-Phase Structure
- **Phase 1 (this award):** Core CRM platform — case management, workflow automation, knowledge management. Integration to INPAS, ERM, IRP, and Voya to enable workflow automation.
- **Phase 2 (contract-extension option):** Employer Portal, call center integration, telephony modernization, Employer Content & Channel Management.
- **Phase 3 (contract-extension option):** Personalized Member Portal, member self-service, omni-channel capability. Assess whether Salesforce becomes the public website/portal front-end.

## Key Constraint
INPRS will purchase Salesforce licenses **outside this contract** — vendor pricing covers implementation, customization, training, and support only, not licensing.`,
          createdAt: d(-45),
          updatedAt: d(-10),
        },
        {
          id: "doc-frd",
          type: "frd",
          title: "CRM & Case Management Scope — RFP 23-03 §3.2-3.3",
          content: `# CRM & Case/Workflow Functional Scope
**Source:** RFP 23-03, Section 3.2 (CRM General Scope) and 3.3 (Case/Workflow General Scope)

## CRM General Scope
- **Contact Management:** unified customer-tracking system covering both members and employers
- **Customer Service & Support:** case tracking/management, SLA monitoring, internal knowledge base, foundation for future self-service
- **Marketing/Communication Automation:** campaign management, email communication, member/employer segmentation
- **Reporting & Analytics:** custom dashboards per role, scheduled reports, real-time analytics
- **Collaboration:** Salesforce Chatter (or similar) for internal chat/notifications, shared calendars, task management
- **User Groups/Roles:** Members, Employers, INPRS Staff (Member Advocates, Employer Advocates, Multi-Role Users, SFDC Administrators), Third-Party Staff

## Case / Workflow Scope
### FR-CASE-001: Multi-Channel Case Creation
System shall accept case creation via email, web portal, phone, and social media — each channel must be intuitive and aligned with the technical proficiency of INPRS's member base.

### FR-CASE-002: Rules-Based Case Assignment
System shall auto-assign cases to agents/queues based on pension-query nature, member demographics, and issue specificity, with manual override/reassignment available to administrative staff.

### FR-CASE-003: Tiered Case Escalation
System shall automatically promote unresolved cases to higher support tiers based on predetermined time frames and issue complexity — given the sensitivity of pension-related inquiries.

### FR-CASE-004: SLA Enforcement
System shall monitor Service Level Agreements and trigger notifications to agents and supervisors when a case is at risk of breaching its agreed-upon response/resolution time.

### FR-CASE-005: Email-to-Case / Web-to-Case
Incoming emails and web-form submissions must automatically convert to trackable Salesforce cases — no communication may fall through the cracks.

### FR-CASE-006: Knowledge Base
A centralized Salesforce Knowledge repository addressing frequently encountered pension questions, accessible to both agents and (eventually) members for self-resolution.

## Non-Functional
- **Governance:** data access/visibility based on roles and hierarchies; compliance with FedRAMP/HIPAA as needed
- **Documentation:** delivered no later than deployment of each component, format mutually agreed with vendor
- **Data Strategy:** subordinate to INPRS's Master Data Management (MDM) strategy; any data-related decision requires Data Governance Council approval`,
          createdAt: d(-40),
          updatedAt: d(-8),
        },
      ],

      userStories: [
        // DONE — pre-award / discovery
        {
          id: "us-1",
          title: "Strategic planning — modernization goals and KPIs",
          asA: "INPRS project sponsor",
          iWant: "a documented understanding of business goals, the State's modernization vision, and success KPIs",
          soThat: "the selected vendor and INPRS agree on what \"done\" looks like before build starts",
          acceptanceCriteria: [
            "Business goals for INPRS modernization documented and signed off",
            "KPI list defined (implementation timeframe, user adoption rate, data migration accuracy, system uptime, SLA response time, ROI)",
            "State's vision for INPRS modernization captured in writing",
          ],
          priority: "must",
          storyPoints: 5,
          status: "done",
          startDate: d(-50),
          dueDate: d(-42),
          estimatedDays: 8,
        },
        {
          id: "us-2",
          title: "Capability mapping against INPRS process catalog",
          asA: "business analyst",
          iWant: "high-level business process flows mapped against INPRS's existing process catalog (RFP Appendix G)",
          soThat: "we know which processes migrate as-is, which get redesigned, and where the pain points are",
          acceptanceCriteria: [
            "Epics list drafted to realize the Future State vision",
            "Process catalog (small/medium/large) reviewed for migration suitability",
            "Additional pain points beyond the RFP identified and logged",
          ],
          priority: "must",
          storyPoints: 8,
          status: "done",
          startDate: d(-42),
          dueDate: d(-30),
          estimatedDays: 12,
        },
        // IN PROGRESS — Phase 1 core
        {
          id: "us-3",
          title: "Case management core build — creation, assignment, escalation",
          asA: "Member Advocate",
          iWant: "cases created via any channel to be auto-assigned and auto-escalated per SLA rules",
          soThat: "pension-related inquiries never sit unaddressed past their service-level window",
          acceptanceCriteria: [
            "Email-to-Case and Web-to-Case both convert submissions into trackable cases",
            "Assignment rules route by query nature, member demographics, and specificity",
            "Escalation triggers automatically at the predetermined time/complexity threshold",
            "Manual override/reassignment available to admin staff",
          ],
          priority: "must",
          storyPoints: 13,
          status: "in-progress",
          startDate: d(-20),
          dueDate: d(15),
          estimatedDays: 35,
        },
        {
          id: "us-4",
          title: "MuleSoft integration — INPAS, ERM, IRP",
          asA: "SFDC Administrator",
          iWant: "Salesforce integrated to INPAS/ERM/IRP via MuleSoft APIs",
          soThat: "workflow automation has the transactional data it needs without manual re-entry",
          acceptanceCriteria: [
            "Integration inventory mapped to business capabilities",
            "APIs written natively in MuleSoft or gatewayed through it (per RFP §3.4)",
            "ERM remains the employer-demographic ingestion engine for Phase 1",
            "Initial data load completed and validated",
          ],
          priority: "must",
          storyPoints: 13,
          status: "in-progress",
          startDate: d(-15),
          dueDate: d(20),
          estimatedDays: 35,
        },
        {
          id: "us-5",
          title: "Voya Salesforce sync — Tier 1/Tier 2 member 360 view",
          asA: "Member Advocate",
          iWant: "INPRS's Salesforce instance kept in sync with Voya's separate Salesforce instance",
          soThat: "agents see a complete member picture across both record-keeping tiers, not a fragmented one",
          acceptanceCriteria: [
            "Data mapped and correlated between INPRS's and Voya's Salesforce orgs",
            "High-level data flow documented between the two systems",
            "Sync approach approved by the Data Governance Council",
            "360-degree member view validated in a test case spanning both tiers",
          ],
          priority: "must",
          storyPoints: 13,
          status: "in-progress",
          startDate: d(-10),
          dueDate: d(25),
          estimatedDays: 35,
        },
        {
          id: "us-6",
          title: "Knowledge base for frequent pension queries",
          asA: "Member Advocate",
          iWant: "a searchable Salesforce Knowledge library of common pension questions",
          soThat: "repetitive queries get resolved faster and eventually support member self-service",
          acceptanceCriteria: [
            "Knowledge articles cover the top recurring case categories",
            "Accessible to agents at case-resolution time",
            "Structured for eventual member-facing self-service reuse in Phase 3",
          ],
          priority: "should",
          storyPoints: 5,
          status: "in-progress",
          startDate: d(0),
          dueDate: d(20),
          estimatedDays: 20,
        },
        // BACKLOG — Phase 1 close-out + Phase 2/3 options
        {
          id: "us-7",
          title: "Salesforce admin knowledge transfer to INPRS IT",
          asA: "SFDC Administrator",
          iWant: "full admin training so INPRS IT can take over 100% Salesforce administration",
          soThat: "INPRS isn't dependent on the vendor after the engagement ends",
          acceptanceCriteria: [
            "INPRS IT staff trained on Phase 1 admin skills",
            "Training also covers skills needed for anticipated Phase 2/3 scope",
            "Multi-tiered support docs delivered (self-service FAQ through live assistance)",
          ],
          priority: "must",
          storyPoints: 8,
          status: "backlog",
          startDate: d(25),
          dueDate: d(45),
          estimatedDays: 20,
        },
        {
          id: "us-8",
          title: "Phase 2 option — Employer Portal + telephony modernization",
          asA: "Employer Advocate",
          iWant: "an Employer Portal with integrated call center and modernized telephony",
          soThat: "employers get a unified reporting/case channel instead of email and phone-only support",
          acceptanceCriteria: [
            "Contract-extension option scoped and priced separately from Phase 1",
            "Employer Content & Channel Management capability defined",
            "Preliminary estimate delivered per RFP §2.3 even though not part of this award",
          ],
          priority: "could",
          storyPoints: 13,
          status: "backlog",
          startDate: d(60),
          dueDate: d(120),
          estimatedDays: 60,
        },
        {
          id: "us-9",
          title: "Phase 3 option — Member Portal + self-service",
          asA: "member",
          iWant: "a personalized Member Portal with self-service and omni-channel access",
          soThat: "I can resolve common requests without contacting an advocate directly",
          acceptanceCriteria: [
            "Preliminary estimate delivered alongside Phase 1 proposal per RFP §2.3",
            "Assessment completed on whether Salesforce becomes the public site/portal front-end",
            "Reuses the Phase 1 knowledge base as the self-service content foundation",
          ],
          priority: "could",
          storyPoints: 13,
          status: "backlog",
          startDate: d(90),
          dueDate: d(150),
          estimatedDays: 60,
        },
      ],
    },
    { headers: auth }
  );

  const projectId: string = project.id;

  const tryPost = async (url: string, body: object) => {
    try {
      const { data } = await axios.post(url, body, { headers: auth });
      return data;
    } catch {
      return null;
    }
  };

  // ── 2. Requirements ──────────────────────────────────────────────────────────
  const requirements = [
    {
      title: "1% customization ceiling",
      raw_text: "The solution must adhere to a customization footprint of no more than 1%. Vendors must leverage Salesforce's built-in capabilities over custom-coded solutions wherever possible (RFP §2.2).",
      req_type: "constraint", priority: "must", domain: "architecture",
    },
    {
      title: "MuleSoft-only integration path",
      raw_text: "All system integrations must be accomplished through APIs managed within MuleSoft — either written natively in MuleSoft or gatewayed through it. No direct point-to-point integrations outside this pattern (RFP §3.4).",
      req_type: "constraint", priority: "must", domain: "integration",
    },
    {
      title: "Voya Salesforce instance sync",
      raw_text: "INPRS's Salesforce instance must be kept in sync with Voya's separate Salesforce instance (third-party Tier 1 record keeper), enabling a 360-degree member view across both tiers.",
      req_type: "functional", priority: "must", domain: "integration",
    },
    {
      title: "Data Governance Council approval for data decisions",
      raw_text: "Any data-related decision — golden-record design, PII access mapping, MDM architecture — must be approved by INPRS's Data Governance Council before implementation.",
      req_type: "constraint", priority: "must", domain: "governance",
    },
    {
      title: "100% admin knowledge transfer",
      raw_text: "INPRS staff must take over 100% administration of Salesforce after the engagement. The vendor must train INPRS's IT staff during Phase 1, including skills needed for anticipated Phase 2/3 work.",
      req_type: "non-functional", priority: "must", domain: "training",
    },
    {
      title: "SLA breach notification",
      raw_text: "The system must monitor case Service Level Agreements and trigger notifications to agents and supervisory personnel when a case is at risk of breaching its agreed-upon response/resolution time.",
      req_type: "functional", priority: "must", domain: "case-management",
    },
    {
      title: "Compliance — FedRAMP/HIPAA as needed",
      raw_text: "Data access and visibility must be based on roles and hierarchies. Compliance with FedRAMP and HIPAA is required as needed given the sensitivity of member pension and health-adjacent data.",
      req_type: "non-functional", priority: "must", domain: "compliance",
    },
  ];

  for (const req of requirements) {
    await tryPost(`${BASE}/api/tools/requirements`, { ...req, project_id: projectId });
  }

  // ── 3. CAPA entries ───────────────────────────────────────────────────────
  const capas = [
    {
      project_id: projectId,
      title: "Member data mismatch found between INPRS and Voya Salesforce instances during sync testing",
      description: "During integration testing of the Tier 1/Tier 2 360-degree member view, 12 test member records showed conflicting contact information between INPRS's Salesforce org and Voya's — no single record was authoritative.",
      root_cause: "No golden-record ownership was formally assigned before sync design began; both systems treated their own contact fields as authoritative, per RFP's noted risk in §2.3 (\"multiple layers of security inherent to INPRS's system design, and the high level of business process integration with Voya's Salesforce instance\").",
      corrective_action: "Escalate golden-record ownership decision to the Data Governance Council per RFP data-strategy requirement; INPRS's Salesforce instance designated authoritative for contact fields, Voya's for Tier 1 transaction history.",
      preventive_action: "Add a Data Governance Council sign-off gate to the Definition of Done for any future cross-system sync work, not just the initial Voya integration.",
      status: "open",
      priority: "high",
      due_date: d(10),
      raised_by: "SFDC Administrators",
    },
    {
      project_id: projectId,
      title: "Case escalation firing before SLA threshold on complex pension queries",
      description: "QA found tiered case escalation triggering roughly 20% early on cases flagged as 'complex' — agents were being paged before the SLA window had actually elapsed.",
      root_cause: "Escalation timer was keyed off case-creation timestamp rather than last-agent-response timestamp, so cases with an initial quick acknowledgment but slow follow-up were mistimed.",
      corrective_action: "Rebase the escalation timer to last-agent-response time; retest against the full complexity-tier matrix from RFP §3.3.",
      preventive_action: "Add explicit test cases for each complexity tier × channel combination to the Definition of Done for case-management stories.",
      status: "open",
      priority: "medium",
      due_date: d(14),
      raised_by: "Member Advocates",
    },
  ];

  for (const capa of capas) {
    await tryPost(`${BASE}/api/tools/capa`, capa);
  }

  // ── 4. Suppliers ─────────────────────────────────────────────────────────────
  const suppliers = [
    {
      project_id: projectId,
      name: "Salesforce",
      category: "SaaS Platform Vendor",
      risk_tier: "critical",
      contact_email: "",
      notes: "Service Cloud Unlimited (x80), Customer Community Unlimited for Members (x4,500), Service Cloud Voice (x34), Government Cloud Plus, Salesforce Shield, OwnBackup. INPRS purchases licenses directly — outside the implementation contract.",
    },
    {
      project_id: projectId,
      name: "MuleSoft",
      category: "Integration Platform",
      risk_tier: "critical",
      contact_email: "",
      notes: "All system integrations (INPAS, ERM, IRP, Voya) must route through MuleSoft per RFP §3.4 — mandatory integration pattern, not a vendor choice.",
    },
    {
      project_id: projectId,
      name: "Voya Financial",
      category: "Third-Party Record Keeper",
      risk_tier: "critical",
      contact_email: "",
      notes: "Maintains the Tier 1 member-support Salesforce instance. Highest-risk external dependency — sync design requires Data Governance Council sign-off (see CAPA-1).",
    },
    {
      project_id: projectId,
      name: "OCM Vendor (TBD — separate RFP)",
      category: "Organizational Change Management",
      risk_tier: "major",
      contact_email: "",
      notes: "INPRS is issuing a separate RFP for OCM per §3.7. The CRM implementation vendor must coordinate training content/delivery with whichever OCM vendor is selected there.",
    },
  ];

  for (const supplier of suppliers) {
    await tryPost(`${BASE}/api/tools/suppliers`, supplier);
  }

  // ── 5. Test Cases ────────────────────────────────────────────────────────────
  const testCases = [
    {
      project_id: projectId,
      title: "Multi-channel case creation — email, web, phone",
      description: "Verify a case submitted via each of the three primary channels (email, web portal, phone) correctly creates a trackable Salesforce case with no data loss.",
      steps: [
        "Submit a test inquiry via email to the configured case-creation address",
        "Submit a test inquiry via the web portal contact form",
        "Log a test inquiry via phone intake workflow",
        "Confirm all three appear as distinct cases in Salesforce with correct source channel tagged",
      ],
      expected_result: "All 3 channels produce a correctly tagged, complete case record with no manual re-entry required.",
      priority: "must",
      status: "pending",
    },
    {
      project_id: projectId,
      title: "SLA breach notification — at-risk case alerting",
      description: "Confirm agents and supervisors are notified before a case's SLA response window actually breaches, not after.",
      steps: [
        "Create a test case with a known SLA response window (e.g. 4 business hours)",
        "Advance time to 80% of the SLA window with no agent response",
        "Verify an at-risk notification fires to the assigned agent",
        "Advance to 100% with no response and verify supervisor escalation notification fires",
      ],
      expected_result: "At-risk notification fires before breach; supervisor notification fires at/after breach — never silent past the SLA window.",
      priority: "must",
      status: "pending",
    },
    {
      project_id: projectId,
      title: "Voya sync — 360-degree member view accuracy",
      description: "Validate that a member record pulled in INPRS's Salesforce correctly reflects both INPRS-side and Voya-side data with the correct golden-record field ownership per CAPA-1's resolution.",
      steps: [
        "Select a test member present in both INPRS and Voya Salesforce instances",
        "Update contact info on the INPRS side; confirm it does NOT get overwritten by a stale Voya value",
        "Update Tier 1 transaction history on the Voya side; confirm it correctly surfaces in the INPRS 360 view",
        "Confirm no field shows conflicting values between the two systems",
      ],
      expected_result: "Each field's designated golden-record owner (INPRS for contact, Voya for Tier 1 transaction history) is respected with zero conflicting values.",
      priority: "must",
      status: "pending",
    },
    {
      project_id: projectId,
      title: "1% customization audit",
      description: "Confirm the delivered Phase 1 solution stays within the RFP's mandated 1% customization ceiling before UAT sign-off.",
      steps: [
        "Run Salesforce's org-complexity/customization audit tooling against the Phase 1 org",
        "Compare custom code + custom object footprint against total org footprint",
        "Flag any component exceeding the 1% threshold for review",
      ],
      expected_result: "Total customization footprint ≤1%, matching the RFP §2.2 vendor-qualification standard.",
      priority: "should",
      status: "pending",
    },
  ];

  for (const tc of testCases) {
    await tryPost(`${BASE}/api/tools/test-cases`, tc);
  }

  // ── 6. Evolution Log ─────────────────────────────────────────────────────────
  const evolutionEntries = [
    {
      project_id: projectId,
      change_type: "scope_add",
      title: "Sales Cloud offered as optional expansion, not part of Phase 1 base scope",
      description: "RFP §2.3 clarifies the Solution is purely Service Cloud-focused for the base award; Sales Cloud implementation services may be proposed as an optional additional expansion, priced separately.",
      requested_by: "INPRS Procurement (Stanton Lanman)",
      impact: "No change to Phase 1 base scope or budget. Vendors may optionally quote Sales Cloud as an add-on line item.",
      status: "approved",
      created_at: d(-38),
    },
    {
      project_id: projectId,
      change_type: "requirement_change",
      title: "Phases 2 and 3 confirmed as contract-extension options, not initial award scope",
      description: "RFP §2.3: \"Phase 1 will be awarded as a result of this Request for Proposal with Phases 2 and 3 having the potential to be extensions to the initial contract upon successful completion of Phase 1.\" Vendors must still provide preliminary estimates for both.",
      requested_by: "INPRS Procurement (Stanton Lanman)",
      impact: "Phase 2/3 user stories tracked in backlog with preliminary estimates only — not committed budget or timeline until Phase 1 completes successfully.",
      status: "approved",
      created_at: d(-36),
    },
    {
      project_id: projectId,
      change_type: "scope_add",
      title: "Golden-record ownership formally assigned after Voya sync mismatch (CAPA-1)",
      description: "Following the member-data mismatch found in integration testing, the Data Governance Council formally assigned field-level golden-record ownership between INPRS and Voya's Salesforce instances.",
      requested_by: "Data Governance Council",
      impact: "Adds one governance sign-off step to all future cross-system sync work, per CAPA-1's preventive action. No budget impact.",
      status: "approved",
      created_at: d(-5),
    },
  ];

  for (const entry of evolutionEntries) {
    await tryPost(`${BASE}/api/tools/evolution-logs`, entry);
  }

  // ── 7. Knowledge Base documents ──────────────────────────────────────────────
  const kbDocs = [
    {
      project_id: projectId,
      title: "INPRS Fund Structure Reference",
      doc_type: "reference",
      source: "RFP 23-03 §1.3.1 — INPRS Background",
      content: `# INPRS Fund Structure

INPRS was established by Indiana statute in 2011 as an independent body corporate and politic — not a state department, but an independent instrumentality. It administers 16 funds:

## Defined Benefit (DB) Funds
- Public Employees' Defined Benefit Account (PERF DB)
- Teachers' Pre-1996 Defined Benefit Account (TRF Pre-'96 DB)
- Teachers' 1996 Defined Benefit Account (TRF '96 DB)
- 1977 Police Officers' and Firefighters' Retirement Fund ('77 Fund)
- Judges' Retirement System (JRS)
- Excise, Gaming and Conservation Officers' Retirement Fund (EG&C)
- Prosecuting Attorneys' Retirement Fund (PARF)
- Legislators' Defined Benefit Fund (LE DB)

## Defined Contribution (DC) Funds
- Public Employees' Defined Contribution Account (PERF DC)
- My Choice: Retirement Savings Plan for Public Employees (PERF MC DC)
- Teachers' Defined Contribution Account (TRF DC)
- My Choice: Retirement Savings Plan for Teachers (TRF MC DC)
- Legislators' Defined Contribution Fund (LE DC)

## Other Postemployment Benefit (OPEB) Funds
- Special Death Benefit Fund (SDBF)
- Retirement Medical Benefits Account Plan (RMBA)

## Custodial Fund
- Local Public Safety Pension Relief Fund (LPSPR)

This breadth is why the CRM's Contact Management and Case Assignment logic must handle member context across 16 distinct fund types, not a single homogeneous member base.`,
    },
    {
      project_id: projectId,
      title: "KPI Reference — CRM Implementation",
      doc_type: "reference",
      source: "RFP 23-03 §3.2 — Key Performance Indicators",
      content: `# KPIs (RFP-listed, non-exhaustive)

- Implementation Timeframe
- User Adoption Rate
- Data Migration Accuracy
- System Uptime
- Response Time for Support Tickets
- Customer Satisfaction Rate
- Cost per Transaction
- Feature Utilization Rate
- Training Completion Rate
- Error Rate in Processes
- Case Metrics (time to resolve, required interactions to resolve)
- ROI

INPRS explicitly notes this list "should not be considered exhaustive" — the vendor is expected to propose additional KPIs relevant to case management and integration health.`,
    },
    {
      project_id: projectId,
      title: "Salesforce Licensing — Phase 1 Allocation",
      doc_type: "technical",
      source: "RFP 23-03 §3.2 — Current and Future Licensing table",
      content: `# Phase 1 License Allocation (estimated by Salesforce Sales team)

| Product | Quantity |
|---|---|
| Service Cloud — Unlimited Edition | 80 |
| Customer Community — Unlimited Edition (Members) | 4,500 |
| Service Cloud – Voice | 34 |
| Salesforce Scheduler — Unlimited Edition | 10 |
| Customer Community — Unlimited Edition (Logins/month) | 50,000 |
| Government Cloud Plus | 1 |
| Salesforce Shield | 1 |
| Lightning Platform — Unlimited Edition (Administrators) | 10 |
| OwnBackup for Salesforce | 1 |

**Note:** INPRS purchases these licenses directly, outside the implementation services contract. Vendors quote implementation/customization/training/support only. Vendors should flag any additional licenses/quantities they believe are needed beyond this table.`,
    },
  ];

  for (const doc of kbDocs) {
    await tryPost(`${BASE}/api/tools/documents`, doc);
  }

  // ── 8. Compliance Rules ───────────────────────────────────────────────────
  const rules = [
    {
      project_id: projectId,
      name: "Indiana Access to Public Records Act (APRA)",
      description: "All proposal materials are subject to Indiana's APRA (IC 5-14-3 et seq.) and become public record after contract award, viewable by anyone including competitors — unless a specific statutory exception is claimed per document/section, with a separate redacted version provided for public release.",
      domain: "public-records",
      market: "IN",
      channel: "procurement",
      contains_phi: false,
    },
    {
      project_id: projectId,
      name: "FedRAMP / HIPAA — as-needed data governance",
      description: "Data access and visibility must be based on roles and hierarchies. Compliance with FedRAMP and HIPAA required as needed given the sensitivity of member data spanning 16 pension funds.",
      domain: "compliance",
      market: "US",
      channel: "internal",
      contains_phi: true,
    },
    {
      project_id: projectId,
      name: "Data Governance Council sign-off gate",
      description: "Any data-related decision (MDM architecture, golden-record ownership, PII access mapping) must be reviewed and approved by INPRS's Data Governance Council before implementation, per RFP §2.3.",
      domain: "governance",
      market: "IN",
      channel: "internal",
      contains_phi: false,
    },
  ];

  for (const rule of rules) {
    await tryPost(`${BASE}/api/tools/rules`, rule);
  }

  return projectId;
}
